/**
 * Workflow Engine API client — separate from IVA verification API.
 *
 * Used inside the SDK iframe to:
 * 1. Submit _input after Veriff capture completes
 * 2. Poll execution status until COMPLETED/FAILED
 * 3. Fetch extracted ID details via the self link
 */

import { logger } from "../utils/logger";
import type { WorkflowExecutionResponse } from "../types/workflowEngine";

const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED", "CANCELLED"]);

/** Unwrap the WE API envelope: { data: T, meta: ... } → T */
async function unwrap<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`WE API ${resp.status}: ${text}`);
  }
  const json = await resp.json();
  return (json.data ?? json) as T;
}

/**
 * Submit input to advance the workflow.
 */
export async function submitWorkflowInput(
  baseUrl: string,
  workflowId: number,
  executionId: string,
  payload: { inspectionName?: string; input: Record<string, unknown> },
  authToken?: string
): Promise<WorkflowExecutionResponse> {
  const url = `${baseUrl}/v1/workflows/${workflowId}/executions/${executionId}/_input`;
  logger.log("[WE API] POST", url);

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

  // Only include inspectionName in the body if provided — some steps (e.g. phone)
  // expect just {"input": {...}} with no extra top-level fields.
  const body: Record<string, unknown> = { input: payload.input };
  if (payload.inspectionName) body.inspectionName = payload.inspectionName;

  return unwrap<WorkflowExecutionResponse>(
    await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      // The SDK runs in an iframe at websdk.idme.cc and POSTs to enroll.idme.cc —
      // same-site but cross-origin, so the default 'same-origin' credentials policy
      // omits the `idme-ipa-session` cookie. The legacy authToken Bearer path still
      // works alongside this; whichever is present wins on the BFF side.
      credentials: "include",
    })
  );
}

/**
 * Submit input to advance the workflow — execution-only URL (no workflowId in path).
 * Used by Workflow 16 (SelfieLiveness / face liveness).
 */
export async function submitWorkflowInputByExecutionId(
  baseUrl: string,
  executionId: string,
  payload: { inspectionName: string; input: Record<string, unknown> },
  authToken?: string
): Promise<WorkflowExecutionResponse> {
  const url = `${baseUrl}/v1/workflows/executions/${executionId}/_input`;
  logger.log("[WE API] POST", url);

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

  return unwrap<WorkflowExecutionResponse>(
    await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      credentials: "include",
    })
  );
}

/**
 * Get current execution state.
 */
export async function getWorkflowExecution(
  baseUrl: string,
  workflowId: number,
  executionId: string,
  authToken?: string
): Promise<WorkflowExecutionResponse> {
  const url = `${baseUrl}/v1/workflows/${workflowId}/executions/${executionId}`;

  const headers: Record<string, string> = {};
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

  return unwrap<WorkflowExecutionResponse>(
    await fetch(url, { headers, credentials: "include" })
  );
}

/**
 * Poll execution until it reaches a terminal state (COMPLETED, FAILED, CANCELLED).
 */
export async function pollUntilTerminal(
  baseUrl: string,
  workflowId: number,
  executionId: string,
  authToken?: string,
  options?: { intervalMs?: number; timeoutMs?: number }
): Promise<WorkflowExecutionResponse> {
  const interval = options?.intervalMs ?? 2000;
  const timeout = options?.timeoutMs ?? 120_000;
  const deadline = Date.now() + timeout;

  logger.log("[WE API] Polling execution until terminal state");

  while (Date.now() < deadline) {
    const exec = await getWorkflowExecution(baseUrl, workflowId, executionId, authToken);

    if (TERMINAL_STATUSES.has(exec.status)) {
      logger.log("[WE API] Poll complete, status:", exec.status);
      return exec;
    }

    logger.log("[WE API] Still", exec.status, "screen:", exec.ux?.screen ?? "none");
    await new Promise((r) => setTimeout(r, interval));
  }

  throw new Error("Polling timed out waiting for workflow to complete");
}

/**
 * Fetch extracted legal ID details from the self link.
 *
 * The link comes from result.data.links.self in the COMPLETED response,
 * e.g. "/v1/verifications/legal-ids/019dd6b2-..."
 *
 * We call it through the BFF proxy.
 */
export async function fetchLegalIdDetails(
  baseUrl: string,
  selfLink: string,
  authToken?: string
): Promise<Record<string, unknown>> {
  const url = `${baseUrl}${selfLink}`;
  logger.log("[WE API] Fetching legal ID details:", url);

  const headers: Record<string, string> = {};
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

  const resp = await fetch(url, { headers, credentials: "include" });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`Legal ID details fetch failed: ${resp.status} ${text}`);
  }
  const json = await resp.json();
  return (json.data ?? json) as Record<string, unknown>;
}
